import { Card, Typography, Form, Input, InputNumber, Select, Button, Row, Col, Space, Tag, AutoComplete, DatePicker, Upload, Divider, Modal } from 'antd'
import React, { useEffect, useState } from 'react'
import {
    ArrowLeftOutlined, InfoCircleOutlined, InboxOutlined, FileTextOutlined,
    PlusOutlined, AppstoreAddOutlined, ScanOutlined, DeleteOutlined,
} from "@ant-design/icons"

import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';


import { StockCountService } from '../../../Services/Manage/StockCountService'
import { ItemService } from '../../../Services/Manage/ItemService'
import { StockLedgerService } from '../../../Services/Manage/StockLedgerService'
import { UserService } from '../../../Services/Manage/UserService'
import { ENDPOINT } from '../../../Services/ENDPOINT'
import useMessage from 'antd/es/message/useMessage'
import { useLocation, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'

const { TextArea } = Input;
const { Title, Text } = Typography;

const compactFormStyle = `
  .ant-form-item { margin-bottom: 15px !important; }
  .ant-card-body { padding: 16px 16px !important; }
  .ant-card { margin-bottom: 16px !important; }
  .ant-form-item-label { padding-bottom: 2px !important; }
  .ant-form-item-label > label { height: 20px !important; }
  .cnt-equal-height .ant-upload-drag { height: 132px; }
  .cnt-equal-height textarea.ant-input { height: 132px !important; resize: none; }
`;

const CardTitle = ({ icon, no, text }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "rgba(14, 93, 146, 0.95)" }}>
        {icon}
        <span style={{ fontWeight: 600 }}>{no}. {text}</span>
    </span>
);

const typeOptions = [
    { value: "Full Stock Count", label: "Full Stock Count" },
    { value: "Cycle Count", label: "Cycle Count" },
];

const statusOptions = [
    { value: "Scheduled", label: "Scheduled" },
    { value: "In Progress", label: "In Progress" },
    { value: "Completed", label: "Completed" },
    { value: "Cancelled", label: "Cancelled" },
];

const AddStockCount = () => {
    const [messageApi, msgContext] = useMessage();
    const [form] = Form.useForm();
    const [countNo, setCountNo] = useState("CNT-2024-00001");
    const [warehouseOptions, setWarehouseOptions] = useState([]);

    const [rows, setRows] = useState([]);
    const [allItems, setAllItems] = useState([]);
    const [itemSearch, setItemSearch] = useState("");

    const [scanModalOpen, setScanModalOpen] = useState(false);
    const [scanValue, setScanValue] = useState("");


    const [userMap, setUserMap] = useState({});
    const [stockQtyMap, setStockQtyMap] = useState({});

    const [editCnt, setEditCnt] = useState(null);
    const [saving, setSaving] = useState(false);
    const [documentFiles, setDocumentFiles] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);

    const location = useLocation();
    const navigate = useNavigate();
    const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem("ome_user")) || {}; } catch { return {}; }
    })();
    const currentUserId = currentUser.uid;
    const id = location.state?.id;
    const action = location.state?.action;
    const enableEdit = location.state?.enableEdit;
    const from = location.state?.from || '/inventory/stock-count';
    const isReadOnly = !enableEdit && !!editCnt;
    const firstOf = (v) => (Array.isArray(v) ? v[0] : v);

    const getUserMap = async () => {
        try {
            const request = await UserService.getAllUsers();
            const map = {};
            (request.data || []).forEach((u) => {
                if (u.user_id) map[String(u.user_id)] = u.name;
            });
            setUserMap(map);
        } catch (error) { console.error(error); }
    };

    const getConfigOptions = async () => {
        try {
            const results = await StockCountService.getDistinctValues("warehouse");
            const toOptions = (arr) => (arr || []).map((v) => ({ value: v, label: v }));
            setWarehouseOptions(toOptions(results.data));
        } catch (error) { console.error(error); }
    };

    const getAllItems = async () => {
        try {
            const request = await ItemService.getAllItem();
            const active = (request.data || []).filter((i) => i.is_active);
            setAllItems(active);
        } catch (error) { console.error(error); }
    };

    const getStockQtyMap = async () => {
        try {
            const request = await StockLedgerService.getOnHandMap();
            setStockQtyMap(request.data || {});
        } catch (error) { console.error(error); }
    };

    const getLastCnt = async () => {
        try {
            const request = await StockCountService.getLastCnt();
            let nextNo = "00001";
            if (request?.status && request?.data?.count_no) {
                const seqPart = request.data.count_no.split("-").pop();
                const parsed = parseInt(seqPart, 10);
                if (!isNaN(parsed)) nextNo = String(parsed + 1).padStart(5, "0");
            }
            setCountNo(`CNT-${dayjs().format("YYYY")}-${nextNo}`);
        } catch (error) { console.error(error); }
    };

    const recalcRow = (row) => {
        const system_qty = Number(row.system_qty || 0);
        const counted_qty = Number(row.counted_qty || 0);
        const variance_qty = counted_qty - system_qty;
        const variance_value = variance_qty * Number(row.unit_cost || 0);
        return { ...row, variance_qty, variance_value };
    };

    const getCntById = async (id) => {
        try {
            const request = await StockCountService.getCntById(id);
            const initialValues = request.data;
            setEditCnt(initialValues);
            setCountNo(initialValues.count_no);
            setRows((initialValues.items || []).map((it, idx) => recalcRow({ ...it, key: idx })));

            let parsedAttachments = [];
            try {
                parsedAttachments = initialValues.attachments
                    ? (typeof initialValues.attachments === "string" ? JSON.parse(initialValues.attachments) : initialValues.attachments)
                    : [];
            } catch { parsedAttachments = []; }

            setExistingAttachments(
                (parsedAttachments || []).map((f, idx) => ({
                    uid: f.uid || `existing-${idx}`,
                    name: f.name || f.filename || `file-${idx}`,
                    ...f,
                    status: "done",
                    url: `${ENDPOINT.base}/${f.path || f.url}`,
                }))
            );

            form.setFieldsValue({
                ...initialValues,
                warehouse: initialValues.warehouse ? [initialValues.warehouse] : [],
                count_date: initialValues.count_date ? dayjs(initialValues.count_date) : null,
                reference_date: initialValues.reference_date ? dayjs(initialValues.reference_date) : null,
            });
        } catch (error) { console.error(error); }
    };

    useEffect(() => {
        getConfigOptions();
        getAllItems();
        getUserMap();
        getStockQtyMap();
        if (id) {
            getCntById(id);
        } else {
            getLastCnt();
            form.setFieldsValue({ count_type: "Full Stock Count", status: "Scheduled", count_by: currentUser?.name || "" });
        }
    }, [id]);

    useEffect(() => {
        if (!Object.keys(stockQtyMap).length) return;
        setRows((prev) => prev.map((row) => (
            row.item_id ? recalcRow({ ...row, system_qty: stockQtyMap[String(row.item_id)] || 0 }) : row
        )));
    }, [stockQtyMap]);

    const uploadOneFile = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch(`${ENDPOINT.StockCount}/upload-file`, { method: "POST", body: formData });
        const result = await res.json();
        return result?.status && result?.success ? result.file : null;
    };

    const handleRowChange = (key, field, value) => {
        setRows((prev) => prev.map((row) => {
            if (row.key !== key) return row;
            const updated = { ...row, [field]: value };
            return (field === "counted_qty" || field === "system_qty" || field === "unit_cost") ? recalcRow(updated) : updated;
        }));
    };

    const handleAddRow = () => {
        setRows((prev) => [...prev, {
            key: Date.now(), item_id: null, item_name: "", description: "", hsn_sku: "",
            unit: "", system_qty: 0, counted_qty: 0, variance_qty: 0, unit_cost: 0, comments: "",
        }]);
    };

    const handleDeleteRow = (key) => {
        setRows((prev) => prev.filter((r) => r.key !== key));
    };

    const handleSelectItemForRow = (key, itemId) => {
        const selected = stockItems.find((x) => Number(x.id) === Number(itemId));
        if (!selected) return;
        setRows((prev) => prev.map((row) => {
            if (row.key !== key) return row;
            const system_qty = stockQtyMap[String(selected.id)] || 0;
            return recalcRow({
                ...row,
                item_id: selected.id,
                item_name: selected.item_name,
                description: selected.description || "",
                hsn_sku: selected.hsn_sac_code || selected.sku_barcode || "",
                unit: selected.unit || "",
                unit_cost: Number(selected.cost_price || 0),
                system_qty,
                counted_qty: system_qty,
            });
        }));
        setItemSearch("");
    };

    const handleClearItemRow = (key) => {
        setRows((prev) => prev.map((row) => {
            if (row.key !== key) return row;
            return recalcRow({
                ...row,
                item_id: null,
                item_name: "",
                description: "",
                hsn_sku: "",
                unit: "",
                unit_cost: 0,
                system_qty: 0,
                counted_qty: 0,
            });
        }));
        setItemSearch("");
    };

    // ADD (new function)
    const handleBarcodeScanned = (rawValue) => {
        const code = (rawValue || "").trim();
        if (!code) {
            messageApi.warning("Please enter a barcode number");
            return;
        }

        const matched = stockItems.find((i) => String(i.barcode || i.sku_barcode).trim() === code);

        if (!matched) {
            messageApi.warning(`No item with stock found for barcode: ${code}`);
            return;
        }

        const emptyRow = rows.find((r) => !r.item_id);
        if (emptyRow) {
            handleSelectItemForRow(emptyRow.key, matched.id);
        } else {
            const system_qty = stockQtyMap[String(matched.id)] || 0;
            const newRow = recalcRow({
                key: Date.now(),
                item_id: matched.id,
                item_name: matched.item_name,
                description: matched.description || "",
                hsn_sku: matched.hsn_sac_code || matched.sku_barcode || "",
                unit: matched.unit || "",
                unit_cost: Number(matched.cost_price || 0),
                system_qty,
                counted_qty: system_qty,
                comments: "",
            });
            setRows((prev) => [...prev, newRow]);
        }

        messageApi.success(`Added: ${matched.item_name}`);
        setScanValue("");
    };

    const stockItems = allItems.filter((i) => (stockQtyMap[String(i.id)] || 0) > 0);

    const itemAutoOptions = stockItems
        .filter((i) => !itemSearch || i.item_name.toLowerCase().includes(itemSearch.toLowerCase()))
        .map((i) => ({ value: String(i.id), label: `${i.item_name}${i.sku_barcode ? ` (${i.sku_barcode})` : ""}` }));

    const totalItems = rows.length;
    const totalSystemQty = rows.reduce((sum, r) => sum + Number(r.system_qty || 0), 0);
    const totalCountedQty = rows.reduce((sum, r) => sum + Number(r.counted_qty || 0), 0);
    const totalVarianceQty = rows.reduce((sum, r) => sum + Number(r.variance_qty || 0), 0);
    const totalVarianceValue = rows.reduce((sum, r) => sum + Number(r.variance_value || 0), 0);

    const positiveCount = rows.filter((r) => Number(r.variance_qty || 0) > 0).length;
    const negativeCount = rows.filter((r) => Number(r.variance_qty || 0) < 0).length;
    const noVarianceCount = rows.filter((r) => Number(r.variance_qty || 0) === 0).length;

    const onFinish = async (values) => {
        await performSave(values);
    };

    const handleSaveDraft = async () => {
        const values = form.getFieldsValue();
        await performSave(values);
    };

    const performSave = async (values) => {
        if (!rows.length) {
            messageApi.warning("Please add at least one item to count");
            return;
        }
        setSaving(true);
        try {
            let uploadedAttachments = [...existingAttachments];
            for (const f of documentFiles) {
                const uploaded = await uploadOneFile(f);
                if (uploaded) uploadedAttachments.push(uploaded);
            }

            const payload = {
                attachments: JSON.stringify(uploadedAttachments),
                ...values,
                warehouse: firstOf(values.warehouse),
                count_no: countNo,
                count_date: values.count_date ? dayjs(values.count_date).format("YYYY-MM-DD") : null,
                reference_date: values.reference_date ? dayjs(values.reference_date).format("YYYY-MM-DD") : null,
                status: values.status || editCnt?.status || "Scheduled",
                added_by: editCnt ? editCnt.added_by : currentUserId,
                items: rows.map(({ key, variance_value, ...rest }) => rest),
            };

            let request;
            if (editCnt) {
                request = await StockCountService.updateCnt(payload, id);
            } else {
                request = await StockCountService.createCnt(payload);
            }

            if (request?.status) {
                messageApi.success(request.message);
                navigate(from);
            } else {
                messageApi.error(request?.message || "Something went wrong");
            }
        } catch (error) {
            console.error("Error in onFinish:", error);
            messageApi.error("Unexpected error occurred");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            const request = await StockCountService.deleteCnt(id);
            if (request.status) {
                messageApi.success(request.message);
                navigate(from);
            } else {
                messageApi.error(request.message);
            }
        } catch (error) { console.error(error); }
    };

    return (
        <>
            <style>{compactFormStyle}</style>
            {msgContext}
            <Card
                title={
                    <Space align="center">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <ArrowLeftOutlined style={{ cursor: "pointer", fontSize: "20px" }} onClick={() => navigate(from)} />
                            <Title level={4} style={{ margin: 0 }}>
                                <Space>
                                    {editCnt ? `${action} Stock Count` : 'New Stock Count'}{' '}
                                    {editCnt && (
                                        <Tag color="cyan" style={{ fontSize: '16px', padding: '6px 16px', borderRadius: '8px', fontWeight: 600 }}>
                                            {editCnt.count_no}
                                        </Tag>
                                    )}
                                </Space>
                            </Title>
                        </div>
                    </Space>
                }
                extra={
                    action === 'View' ? null : !enableEdit && editCnt ? (
                        <Button size="large" danger onClick={() => navigate("/inventory/stock-count/edit-stock-count", { state: { id, action: 'Edit', enableEdit: true, from } })}>
                            Enable Edit
                        </Button>
                    ) : (
                        <Space>
                            <Button size="large" loading={saving} onClick={handleSaveDraft}>Save as Draft</Button>
                            <Button
                                type="primary" size="large" loading={saving} onClick={() => form.submit()}
                                style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                            >
                                {editCnt ? "Update Stock Count" : "Save Stock Count"}
                            </Button>
                        </Space>
                    )
                }
            >
                <Row gutter={16}>
                    <Col xs={24} lg={17}>
                        <Form layout="vertical" form={form} onFinish={onFinish} size="middle" disabled={isReadOnly}>
                            <Card title={<CardTitle icon={<InfoCircleOutlined />} no={1} text="Count Details" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item label="Count No.">
                                            <Input size="large" readOnly value={countNo} placeholder="Auto generate" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="count_date" label="Count Date" rules={[{ required: true, message: 'Please select count date' }]}>
                                            <DatePicker size="large" style={{ width: '100%' }} format="DD-MM-YYYY" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="count_type" label="Count Type" rules={[{ required: true, message: 'Please select count type' }]}>
                                            <Select options={typeOptions} size="large" placeholder="Select type" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="warehouse" label="Warehouse" rules={[{ required: true, message: 'Please select warehouse' }]}>
                                            <Select options={warehouseOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type warehouse" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="reference_no" label="Reference No. (Optional)">
                                            <Input size="large" placeholder="Enter reference number" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="reference_date" label="Reference Date (Optional)">
                                            <DatePicker size="large" style={{ width: '100%' }} format="DD-MM-YYYY" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="count_by" label="Count By">
                                            <Input size="large" value={currentUser?.name || ""} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="status" label="Status">
                                            <Select options={statusOptions} size="large" placeholder="Select status" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={12}>
                                        <Form.Item name="description" label="Description (Optional)">
                                            <TextArea rows={3} maxLength={250} showCount placeholder="Enter description" style={{ resize: 'none' }} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card
                                title={<CardTitle icon={<InfoCircleOutlined />} no={2} text="Items" />}
                                extra={
                                    <Space>
                                        <Button icon={<PlusOutlined />} type="primary" onClick={handleAddRow}
                                            style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}>
                                            Add Item
                                        </Button>
                                        {/* <Button icon={<AppstoreAddOutlined />}>Add Multiple Items</Button> */}
                                        <Button icon={<ScanOutlined />} onClick={() => setScanModalOpen(true)}>Scan Barcode</Button>
                                    </Space>
                                }
                            >
                                {rows.length > 0 && (
                                    <Row gutter={8} style={{ padding: "0 8px", marginBottom: 6 }}>
                                        <Col xs={0} md={1}><Text type="secondary" strong style={{ fontSize: 12 }}>#</Text></Col>
                                        <Col xs={24} md={5}>
                                            <Text type="secondary" strong style={{ fontSize: 12, display: 'block' }}>Item / Product</Text>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Description</Text>
                                        </Col>
                                        <Col xs={12} md={3}>
                                            <Text type="secondary" strong style={{ fontSize: 12, display: 'block' }}>HSN / SKU</Text>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Unit</Text>
                                        </Col>
                                        <Col xs={6} md={3}><Text type="secondary" strong style={{ fontSize: 12 }}>System Qty (On Hand)</Text></Col>
                                        <Col xs={6} md={3}><Text type="secondary" strong style={{ fontSize: 12 }}>Counted Qty</Text></Col>
                                        <Col xs={6} md={2}><Text type="secondary" strong style={{ fontSize: 12 }}>Variance Qty</Text></Col>
                                        <Col xs={6} md={2}><Text type="secondary" strong style={{ fontSize: 12 }}>Variance Value (₹)</Text></Col>
                                        <Col xs={6} md={1}></Col>
                                    </Row>
                                )}

                                {rows.map((row, idx) => (
                                    <Row key={row.key} gutter={8} align="middle" style={{ marginBottom: 8, border: "1px solid #f0f0f0", borderRadius: 8, padding: 8 }}>
                                        <Col xs={0} md={1}><Text type="secondary">{idx + 1}</Text></Col>
                                        <Col xs={24} md={5}>
                                            {row.item_id ? (
                                                <>
                                                    <div style={{ fontWeight: 600, fontSize: 13 }}>{row.item_name}</div>
                                                    <div style={{ fontSize: 12, color: '#888' }}>{row.description}</div>
                                                </>
                                            ) : (
                                                // <AutoComplete
                                                //     style={{ width: "100%" }}
                                                //     options={itemAutoOptions}
                                                //     value={row.item_name}
                                                //     placeholder="Type item / product"
                                                //     filterOption={false}
                                                //     allowClear
                                                //     onSearch={(val) => setItemSearch(val)}
                                                //     onSelect={(val) => handleSelectItemForRow(row.key, val)}
                                                //     onChange={(val) => handleRowChange(row.key, "item_name", val)}
                                                // />

                                                <AutoComplete
                                                    style={{ width: "100%" }}
                                                    options={itemAutoOptions}
                                                    value={row.item_name}
                                                    placeholder="Search item / product"
                                                    filterOption={false}
                                                    allowClear
                                                    notFoundContent="No matched items / products"
                                                    onSearch={(val) => {
                                                        setItemSearch(val);
                                                        handleRowChange(row.key, "item_name", val);
                                                    }}
                                                    onSelect={(val) => handleSelectItemForRow(row.key, val)}
                                                    onClear={() => handleClearItemRow(row.key)}
                                                    onBlur={() => {
                                                        if (!row.item_id) {
                                                            handleClearItemRow(row.key);
                                                        }
                                                    }}
                                                />
                                            )}
                                        </Col>
                                        <Col xs={12} md={3}>
                                            <div style={{ fontSize: 13 }}>{row.hsn_sku}</div>
                                            <div style={{ fontSize: 12, color: '#888' }}>{row.unit}</div>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <InputNumber min={0} style={{ width: '100%' }} value={row.system_qty} readOnly />
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <InputNumber min={0} style={{ width: '100%' }} value={row.counted_qty}
                                                onChange={(v) => handleRowChange(row.key, "counted_qty", v)} />
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <span style={{ fontWeight: 600, color: Number(row.variance_qty || 0) === 0 ? '#888' : Number(row.variance_qty || 0) > 0 ? '#52c41a' : '#f5222d' }}>
                                                {Number(row.variance_qty || 0) > 0 ? '+' : ''}{Number(row.variance_qty || 0).toFixed(2)}
                                            </span>
                                        </Col>
                                        <Col xs={6} md={2}>
                                            <span style={{ fontWeight: 600, color: Number(row.variance_value || 0) === 0 ? '#888' : Number(row.variance_value || 0) > 0 ? '#52c41a' : '#f5222d' }}>
                                                {Number(row.variance_value || 0) > 0 ? '+' : ''}{Number(row.variance_value || 0).toFixed(2)}
                                            </span>
                                        </Col>
                                        <Col xs={6} md={1}>
                                            <Button danger type="text" icon={<DeleteOutlined />} onClick={() => handleDeleteRow(row.key)} />
                                        </Col>
                                    </Row>
                                ))}
                                {rows.length === 0 && (
                                    <Text type="secondary">Add items to record a physical stock count</Text>
                                )}
                                {rows.length > 0 && (
                                    <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>Total Items: {rows.length}</div>
                                )}
                            </Card>

                            <Card title={<CardTitle icon={<FileTextOutlined />} no={3} text="Additional Information" />} className="cnt-equal-height">
                                <Row gutter={16}>
                                    <Col xs={24} md={12}>
                                        <Form.Item label="Attachments (Optional)">
                                            <Upload.Dragger
                                                multiple
                                                beforeUpload={(file) => { setDocumentFiles((prev) => [...prev, file]); return false; }}
                                                onRemove={(file) => {
                                                    if (file.status === "done") {
                                                        setExistingAttachments((prev) => prev.filter((f) => f.uid !== file.uid));
                                                    } else {
                                                        setDocumentFiles((prev) => prev.filter((f) => f.uid !== file.uid));
                                                    }
                                                }}
                                                fileList={[...existingAttachments, ...documentFiles]}
                                            >
                                                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                                                <p className="ant-upload-text">Click or drag files to upload</p>
                                                <p className="ant-upload-hint">PNG, JPG, PDF (Max. 5MB)</p>
                                            </Upload.Dragger>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="notes" label="Notes (Optional)">
                                            <TextArea maxLength={250} showCount placeholder="Enter notes (optional)" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Form>

                        {!isReadOnly && (
                            <div style={{ display: 'flex', justifyContent: 'right', marginTop: 8 }}>
                                <Space>
                                    <Button size='large' onClick={() => navigate(from)}>Cancel</Button>
                                    {editCnt && (
                                        <Button size="large" danger onClick={handleDelete}>Delete</Button>
                                    )}
                                    <Button
                                        type="primary" size="large" loading={saving} onClick={() => form.submit()}
                                        style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                                    >
                                        {editCnt ? "Update Stock Count" : "Save Stock Count"}
                                    </Button>
                                </Space>
                            </div>
                        )}

                        {editCnt && (
                            <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: '#999' }}>
                                Created by: {userMap[String(editCnt.added_by)] || editCnt.added_by || '-'}
                                {'  |  '}
                                Last updated: {editCnt.updated_at ? dayjs(editCnt.updated_at).format('DD-MM-YYYY HH:mm') : '-'}
                            </div>
                        )}
                    </Col>

                    <Col xs={24} lg={7}>
                        <Card title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Count Summary</span>} size="small">
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {[
                                    ["Total Items", totalItems],
                                    ["Total System Qty", totalSystemQty.toFixed(2)],
                                    ["Total Counted Qty", totalCountedQty.toFixed(2)],
                                ].map(([label, value]) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                        <Text type="secondary">{label}</Text>
                                        <Text strong>{value}</Text>
                                    </div>
                                ))}
                                <Divider style={{ margin: "6px 0" }} />
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                    <Text type="secondary">Total Variance Qty</Text>
                                    <Text strong style={{ color: totalVarianceQty === 0 ? '#333' : totalVarianceQty > 0 ? '#52c41a' : '#f5222d' }}>
                                        {totalVarianceQty > 0 ? '+' : ''}{totalVarianceQty.toFixed(2)}
                                    </Text>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                    <Text type="secondary">Total Variance Value (₹)</Text>
                                    <Text strong style={{ color: totalVarianceValue === 0 ? '#333' : totalVarianceValue > 0 ? '#52c41a' : '#f5222d' }}>
                                        {totalVarianceValue > 0 ? '+' : ''}{totalVarianceValue.toLocaleString('en-IN')}
                                    </Text>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, alignItems: 'center' }}>
                                    <Text type="secondary">Status</Text>
                                    <Tag color="blue">{editCnt?.status || 'Scheduled'}</Tag>
                                </div>
                            </Space>
                        </Card>

                        <Card style={{ marginTop: 16 }} size="small" title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Variance Summary</span>}>
                            {totalItems === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13 }}>
                                    <span style={{ fontSize: 28 }}>📊</span>
                                    <span style={{ marginTop: 6 }}>No data yet</span>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <div style={{ width: 110, height: 110, flexShrink: 0, position: 'relative' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <RechartsTooltip contentStyle={{ fontSize: 11, padding: '4px 8px', borderRadius: 6 }} />
                                                <Pie
                                                    data={[
                                                        { name: 'Positive Variance', value: positiveCount, color: '#52c41a' },
                                                        { name: 'Negative Variance', value: negativeCount, color: '#f5222d' },
                                                        { name: 'No Variance', value: noVarianceCount, color: '#faad14' },
                                                    ].filter((d) => d.value > 0)}
                                                    dataKey="value" nameKey="name" innerRadius={34} outerRadius={52} paddingAngle={2}
                                                >
                                                    {[
                                                        { name: 'Positive Variance', value: positiveCount, color: '#52c41a' },
                                                        { name: 'Negative Variance', value: negativeCount, color: '#f5222d' },
                                                        { name: 'No Variance', value: noVarianceCount, color: '#faad14' },
                                                    ].filter((d) => d.value > 0).map((e, i) => <Cell key={i} fill={e.color} />)}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                                            <div style={{ fontSize: 18, fontWeight: 700, color: '#333', lineHeight: 1 }}>{totalItems}</div>
                                            <div style={{ fontSize: 9, color: '#888' }}>Total</div>
                                        </div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        {[
                                            ['Positive Variance', positiveCount, '#52c41a'],
                                            ['Negative Variance', negativeCount, '#f5222d'],
                                            ['No Variance', noVarianceCount, '#faad14'],
                                        ].map(([label, value, color], i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, marginBottom: 6 }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <span style={{ color, fontSize: 14 }}>●</span>
                                                    <span style={{ color: '#333' }}>{label}</span>
                                                </span>
                                                <span style={{ fontWeight: 700, color: '#333' }}>{value} ({totalItems ? Math.round((value / totalItems) * 100) : 0}%)</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Card>

                        <Card style={{ marginTop: 16 }} size="small" title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Important Notes</span>}>
                            <div style={{ fontSize: 12, color: '#666' }}>
                                <div style={{ marginBottom: 6 }}>✓ Ensure all items are counted physically.</div>
                                <div style={{ marginBottom: 6 }}>✓ Counted quantity will be compared with system quantity.</div>
                                <div style={{ marginBottom: 6 }}>✓ Variance will be updated after saving and approval.</div>
                                <div>✓ You can review and finalize this count after verification.</div>
                            </div>
                        </Card>
                    </Col>
                </Row>


                <Modal
                    title="Enter Barcode"
                    open={scanModalOpen}
                    onCancel={() => { setScanModalOpen(false); setScanValue(""); }}
                    footer={null}
                    destroyOnClose
                >
                    <Space.Compact style={{ width: "100%" }}>
                        <Input
                            autoFocus
                            size="large"
                            placeholder="Enter barcode number..."
                            value={scanValue}
                            onChange={(e) => setScanValue(e.target.value)}
                            onPressEnter={() => handleBarcodeScanned(scanValue)}
                        />
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => handleBarcodeScanned(scanValue)}
                            style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                        >
                            Enter
                        </Button>
                    </Space.Compact>
                </Modal>
            </Card>
        </>
    );
};

export default AddStockCount;